import React, { useState, useEffect } from 'react';
import LocationFilter from './LocationFilter';

const TABS = [
  { key: 'groups', label: '🔧 Modifier Groups' },
  { key: 'products', label: '📦 Products' },
];

export default function Modifiers({ locationId, locationName }) {
  const [activeTab, setActiveTab] = useState('groups');
  const [locationFilter, setLocationFilter] = useState(locationId || '');
  const [groupsWithModifiers, setGroupsWithModifiers] = useState([]);
  const [groupsWithProducts, setGroupsWithProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    setLocationFilter(locationId || '');
  }, [locationId]);

  useEffect(() => {
    fetchData();
  }, [locationFilter]);

  async function fetchData() {
    try {
      setLoading(true);
      setError(null);
      const query = locationFilter ? `?locationId=${locationFilter}` : '';
      const [modifiersRes, productsRes] = await Promise.all([
        fetch(`/api/modifiers/groups${query}`),
        fetch(`/api/modifiers/products${query}`),
      ]);
      if (!modifiersRes.ok || !productsRes.ok) throw new Error('Failed to fetch modifiers data');
      setGroupsWithModifiers(await modifiersRes.json());
      setGroupsWithProducts(await productsRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function switchTab(tab) {
    setActiveTab(tab);
    setSearch('');
    setExpanded(new Set());
  }

  function toggleGroup(guid) {
    const next = new Set(expanded);
    if (next.has(guid)) {
      next.delete(guid);
    } else {
      next.add(guid);
    }
    setExpanded(next);
  }

  const groups = activeTab === 'groups' ? groupsWithModifiers : groupsWithProducts;
  const filteredGroups = groups.filter((g) => g.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="table-views-container">
      <h2>🔧 Modifiers {locationFilter && locationName ? `- ${locationName}` : ''}</h2>

      <LocationFilter value={locationFilter} onChange={setLocationFilter} />

      <div className="table-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`table-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => switchTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="table-content">
        {loading && <p className="loading">Loading...</p>}
        {error && <div className="error">✕ {error}</div>}

        {!loading && !error && (
          <>
            <div className="search-box">
              <input
                type="text"
                placeholder="Search modifier groups..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
              />
            </div>

            {filteredGroups.length === 0 && <p className="empty">No modifier groups found.</p>}

            <div className="group-tree">
              {filteredGroups.map((group) => (
                <ModifierGroupRow
                  key={group.guid}
                  group={group}
                  activeTab={activeTab}
                  isExpanded={expanded.has(group.guid)}
                  onToggle={() => toggleGroup(group.guid)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ModifierGroupRow({ group, activeTab, isExpanded, onToggle }) {
  const children = activeTab === 'groups' ? group.modifiers : group.products;
  const childLabel = activeTab === 'groups' ? 'modifier' : 'product';

  return (
    <div className="group-node">
      <div className="group-header">
        <button className="expand-btn" onClick={onToggle} title={isExpanded ? 'Collapse' : 'Expand'}>
          {isExpanded ? '▼' : '▶'}
        </button>
        <span className="group-name">{group.name}</span>
        <span className="item-count">{children.length} {childLabel}{children.length !== 1 ? 's' : ''}</span>
        {activeTab === 'groups' && (
          <span className="item-count">min {group.min_selected} / max {group.max_selected}</span>
        )}
      </div>

      {isExpanded && (
        <div className="items-list">
          {children.length === 0 && (
            <p className="empty-section">
              {activeTab === 'groups' ? 'No modifiers' : 'No connected products'}
            </p>
          )}
          {children.map((child, idx) => (
            <div key={`${child.guid}-${idx}`} className="item">
              <div className="item-info">
                <span className="item-name">{child.name}</span>
              </div>
              {activeTab === 'groups' && (
                <span className="item-price">${Number(child.price_adjustment || 0).toFixed(2)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
